import { TestSummary } from 'libs/end-to-end/src/tests-utils';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { OrganizationFocusAreaModes } from 'libs/end-to-end/src/focus-area';
import { RootCausePlotsSummary } from 'libs/end-to-end/src/root-cause-analysis';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { UpsellDefinition } from 'libs/end-to-end/src/types/databases/copilots/upsell-definition';
import { InsightsResult } from 'libs/end-to-end/src/insights';
import { InsightsV2 } from 'libs/end-to-end/src/insights';
import { UniqueColumnMap } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { OverviewPageConfiguration } from 'libs/end-to-end/src/overview-page-configuration';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { TransformInsightsDateColumn } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { TransformInsightsNumericColumn } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { HiringNotificationEmailSubjectSuffixTemplate } from 'libs/end-to-end/src/types/hiring-notification-sender/hiring-notification-email-subject-suffix-template';
import { z } from 'zod';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { EntitlementFeature } from 'libs/end-to-end/src/group-entitlements';
import { CopilotAutoMLConfiguration } from 'libs/end-to-end/src/copilot';
import { CommonRevisionFlags } from 'libs/end-to-end/src/common-revision-flags';
import { MyOrganizationExternalStreamlitAppEntitlements } from 'libs/end-to-end/src/external-streamlit-apps';

export const MyObservation = CommonRevisionFlags.extend({
  type: ObservationLocationType,
  testSummary: TestSummary.nullable(),
  treatments: z.array(Treatment).nullable(),
  features: z.array(EntitlementFeature),
  focusAreaModes: OrganizationFocusAreaModes.optional(),
  rootCausePlots: RootCausePlotsSummary.nullable(),
  dimensions: z.array(DimensionDefinition),
  filterSetDefinitionsWithValues: z.array(FilterSetDefinitionWithValues),
  focusAreas: z.array(MyFocusAreaDefinition),
  upsells: z.array(UpsellDefinition),
  insights: InsightsResult,
  insightsV2: InsightsV2,
  uniqueValuesMapPerSet: z.array(
    z.object({
      filterSetName: z.string(),
      uniqueValuesMap: z.array(UniqueColumnMap),
    }),
  ),
  overviewPageConfiguration: OverviewPageConfiguration,
  calibrationFraming: CalibrationFraming,
  showAllInsights: z.boolean(),
  showNewDataIntelligenceDeepDivePage: z.boolean(),
  showSavedAnalysesPage: z.boolean(),
  showDownloadMultiAnalysisButton: z.boolean(),
  showDownloadSingleAnalysisButton: z.boolean(),
  showDynamicPipelineAnalysisPage: z.boolean(),
  showFobButtonInDashboardPage: z.boolean(),
  showHeatmapAverages: z.boolean(),
  sendHiringNotificationSummaryEmailAfterTopLevelAnalysis: z
    .boolean()
    .optional(),
  hiringNotificationEmailSubjectSuffixTemplate:
    HiringNotificationEmailSubjectSuffixTemplate.optional(),
  forceHiringNotificationSummaryToOnlySendToThisEmail: z
    .string()
    .email()
    .optional(),
  numPreviousDaysOfApplicantsForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  minReadinessRatingForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  additionalEmailsToSendAllHiringNotificationSummaryEmailsTo: z
    .string()
    .email()
    .array()
    .optional(),
  enableAtsWriteBack: z.boolean().optional(),
  hideEmployeeLifecyclePage: z.boolean(),
  hideFocusAreaSolutionsPage: z.boolean(),
  translationPhrasingCode: z.string().optional(),
  showAIExplanations: z.boolean(),
  webCacheKeyPrefix: z.string(),
  numericColumns: z
    .record(z.string(), TransformInsightsNumericColumn)
    .optional(),
  dateColumns: z.record(z.string(), TransformInsightsDateColumn).optional(),
  copilotAutoMLConfiguration: CopilotAutoMLConfiguration.optional(),
  showMyCustomApps: z.boolean(),
  streamlitAppEntitlements: MyOrganizationExternalStreamlitAppEntitlements,
});

export type MyObservation = z.infer<typeof MyObservation>;
